import React, { useEffect } from "react";
import { 
  Box, Typography, Grid, Card, CardMedia, CardContent, 
  Container, Avatar, Paper, Divider, List, ListItem, 
  ListItemIcon, ListItemText, Chip 
} from "@mui/material";
import { 
  DirectionsCar, 
  VerifiedUser, 
  HeadsetMic, 
  AttachMoney, 
  Landscape, 
  Route,
  Star,
  CheckCircle 
} from "@mui/icons-material";


const AboutUs = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Mock Data for Stats and Features
  const stats = [
    { label: "Happy Travelers", value: "5,000+", icon: <Star /> },
    { label: "Destinations", value: "50+", icon: <Landscape /> },
    { label: "Years Experience", value: "10+", icon: <Route /> },
    { label: "Expert Drivers", value: "100+", icon: <DirectionsCar /> },
  ];

  const features = [
    {
      title: "Safe & Reliable",
      desc: "Our vehicles are regularly serviced, and our drivers are background-verified experts.",
      icon: <VerifiedUser sx={{ fontSize: 40, color: '#2e7d32' }} />
    },
    {
      title: "24/7 Support",
      desc: "We are available round the clock to assist you with bookings, route changes, or emergencies.",
      icon: <HeadsetMic sx={{ fontSize: 40, color: '#2e7d32' }} />
    },
    {
      title: "Best Prices",
      desc: "Transparent pricing with no hidden charges. We offer the most competitive rates in Himachal.",
      icon: <AttachMoney sx={{ fontSize: 40, color: '#2e7d32' }} />
    },
    {
      title: "Luxury Fleet",
      desc: "From budget hatchbacks to luxury SUVs and Tempo Travellers, we have a ride for every need.",
      icon: <DirectionsCar sx={{ fontSize: 40, color: '#2e7d32' }} />
    }
  ];

  return (
    <Box sx={{ backgroundColor: "#f4f7f6", pb: 8 }}>
      
           
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: 8, pb: 6 }}>
        <Grid container spacing={4} alignItems="center">
          {/* Image Collage */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Card sx={{ borderRadius: '16px', boxShadow: 4, height: '100%' }}>
                  <CardMedia
                    component="img"
                    height="300"
                    image="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&q=80&w=600"
                    alt="Road Trip"
                    sx={{ objectFit: 'cover' }}
                  />
                </Card>
              </Grid>

              
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
                  <Card sx={{ borderRadius: '16px', boxShadow: 4, flex: 1 }}>
                    <CardMedia
                      component="img"
                      height="140"
                      image="https://maashaktitours.com/wp-content/uploads/2019/06/mera-himachal.jpg"
                      alt="Luxury Car"
                      sx={{ objectFit: 'cover' }}
                    />
                  </Card>
                  <Card sx={{ borderRadius: '16px', boxShadow: 4, flex: 1 }}>
                    <CardMedia
                      component="img"
                      height="140"
                      image="https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?auto=format&fit=crop&q=80&w=600"
                      alt="Mountains"
                      sx={{ objectFit: 'cover' }}
                    />
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          {/* Text Content */}
          <Grid item xs={12} md={6}>
            <Chip label="Who We Are" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold', mb: 2 }} />
            <Typography variant="h3" fontWeight={800} color="#1b5e20" gutterBottom sx={{ lineHeight: 1.2 }}>
              Discover the Heart of Himachal with Himachal Tours with Ajju
            </Typography>
            <Typography variant="body1" color="#555" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              Welcome to <strong>Himachal Tours with Ajju</strong>, your most trusted travel partner for exploring the majestic landscapes of Himachal Pradesh. Founded with a passion for travel and a commitment to safety, we have transformed the way people experience the mountains.
            </Typography>
            <Typography variant="body1" color="#555" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              Whether you are seeking spiritual solace in the temples of Manali, the rugged terrains of Spiti, or the colonial charm of Shimla, our premium taxi services ensure your journey is as memorable as the destination itself.
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <List>
                <ListItem>
                  <ListItemIcon><CheckCircle sx={{ color: '#4caf50' }} /></ListItemIcon>
                  <ListItemText sx={{ color: 'Black' }} primary="Professional & Polite Local Drivers" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle sx={{ color: '#4caf50' }} /></ListItemIcon>
                  <ListItemText sx={{ color: 'Black' }} primary="Sanitized & Comfortable Fleet" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle sx={{ color: '#4caf50' }} /></ListItemIcon>
                  <ListItemText sx={{ color: 'Black' }} primary="Customized Tour Packages" />
                </ListItem>
              </List>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Stats Section */}
      <Box sx={{ bgcolor: '#2e7d32', py: 6, mb: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="space-around">
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index} textAlign="center">
                <Avatar sx={{ bgcolor: '#fff', width: 56, height: 56, mx: 'auto', mb: 2, color: '#2e7d32' }}>
                  {stat.icon}
                </Avatar>
                <Typography variant="h4" fontWeight={800} color="#fff">
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="#e8f5e9" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  {stat.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us Features */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Typography variant="h4" fontWeight={700} color="#1b5e20" textAlign="center" gutterBottom>
          Why Travel With Us?
        </Typography>
        <Typography variant="subtitle1" color="#666" textAlign="center" sx={{ mb: 5 }}>
          We go the extra mile to make your Himalayan journey seamless and stress-free.
        </Typography>
        
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} lg={3} key={index}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 3, 
                  textAlign: 'center', 
                  height: '100%', 
                  borderRadius: '12px',
                  transition: '0.3s',
                  '&:hover': { transform: 'translateY(-5px)', boxShadow: 6 },
                  borderTop: '4px solid #2e7d32'
                }}
              >
                <Box sx={{ mb: 2 }}>
                  {feature.icon}
                </Box>
                <Typography variant="h6" fontWeight={700} gutterBottom color="#333">
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="#666">
                  {feature.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Services Section */}
      <Box sx={{ bgcolor: '#e8f5e9', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
             <Grid item xs={12} md={6}>
                <img 
                  src="https://www.carrentaldelhi.biz/images/car-photoes/swift-dzire/swift-dzire-1.jpg" 
                  alt="Service Car" 
                  style={{ width: '100%', borderRadius: '16px', boxShadow: 3 }}
                />
             </Grid>
             <Grid item xs={12} md={6}>
                <Typography variant="h3" fontWeight={800} color="#1b5e20" gutterBottom>
                  Our Premium Services
                </Typography>
                <Divider sx={{ width: 80, height: 4, bgcolor: '#4caf50', mb: 3 }} />
                
                <Typography variant="body1" paragraph color="#555">
                  We provide comprehensive travel solutions tailored for families, solo travelers, and large groups.
                </Typography>

                <Grid container spacing={2}>
                   <Grid item xs={6}>
                      <Typography variant="h6" fontWeight="bold" color="#2e7d32">Local Sightseeing</Typography>
                      <Typography variant="caption" color="#666">Full day city tours with expert local guides.</Typography>
                   </Grid>
                   <Grid item xs={6}>
                      <Typography variant="h6" fontWeight="bold" color="#2e7d32">Outstation Trips</Typography>
                      <Typography variant="caption" color="#666">Inter-city transfers to Manali, Dalhousie, etc.</Typography>
                   </Grid>
                   <Grid item xs={6}>
                      <Typography variant="h6" fontWeight="bold" color="#2e7d32">Airport Transfers</Typography>
                      <Typography variant="caption" color="#666">Pickup and drop from Chandigarh/Dharamshala airport.</Typography>
                   </Grid>
                   <Grid item xs={6}>
                      <Typography variant="h6" fontWeight="bold" color="#2e7d32">Honeymoon Packages</Typography>
                      <Typography variant="caption" color="#666">Romantic getaways with private cabs and decorations.</Typography>
                   </Grid>
                </Grid>
             </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Our Mission / CTA */}
      <Container maxWidth="md" sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h4" color="Black" fontWeight={700} gutterBottom>
          Ready to Start Your Adventure?
        </Typography>
        <Typography variant="body1" color="#666" paragraph sx={{ mb: 4 }}>
          Experience the beauty of the mountains with the best in the business. Book your ride today and create memories that last a lifetime.
        </Typography>
        {/* Assuming you have a button component, using MUI Button as placeholder */}
        {/* <Button variant="contained" size="large" sx={{ bgcolor: '#2e7d32', px: 4, py: 1.5, borderRadius: 4 }}>Book Now</Button> */}
      </Container>

    </Box>
  );
};

export default AboutUs;